import { useState, useCallback } from 'react';
import {
  getSchedulingThresholds,
  saveSchedulingThresholds,
  type SchedulingThresholds,
} from '@/lib/scheduling-thresholds-storage';

export interface UseSchedulingThresholdsReturn {
  thresholds: SchedulingThresholds;
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  updateThreshold: (key: keyof Omit<SchedulingThresholds, 'lastUpdated'>, value: number) => void;
  saveThresholds: () => Promise<void>;
  resetToDefaults: () => void;
}

export function useSchedulingThresholds(): UseSchedulingThresholdsReturn {
  const [thresholds, setThresholds] = useState<SchedulingThresholds>(getSchedulingThresholds);
  const [savedThresholds, setSavedThresholds] = useState<SchedulingThresholds>(getSchedulingThresholds);
  const [isSaving, setIsSaving] = useState(false);

  const hasUnsavedChanges =
    thresholds.fullyResourcedMin !== savedThresholds.fullyResourcedMin ||
    thresholds.partiallyResourcedMin !== savedThresholds.partiallyResourcedMin;

  const updateThreshold = useCallback(
    (key: keyof Omit<SchedulingThresholds, 'lastUpdated'>, value: number) => {
      setThresholds((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const saveThresholds = useCallback(async () => {
    setIsSaving(true);
    try {
      // Simulate async save (replace with real API call when backend is ready)
      await new Promise((resolve) => setTimeout(resolve, 400));
      saveSchedulingThresholds(thresholds);
      setSavedThresholds(thresholds);
    } finally {
      setIsSaving(false);
    }
  }, [thresholds]);

  const resetToDefaults = useCallback(() => {
    const defaults = { fullyResourcedMin: 95, partiallyResourcedMin: 50 };
    setThresholds((prev) => ({ ...prev, ...defaults }));
  }, []);

  return {
    thresholds,
    hasUnsavedChanges,
    isSaving,
    updateThreshold,
    saveThresholds,
    resetToDefaults,
  };
}
